/**
 * Ongelmakartta v3's facilitator panel (#1389, specs/workshop-modules/systemic_map.md v3,
 * "Vipupisteet"): how long the next run will take (from earlier runs) with each analysis model,
 * the judge off and on, which of the latest run's
 * vipupisteet the room sees (PivotChoice), the workshop's actor list, which a vipupiste's owner and opponents are chosen
 * from (each actor with its other names and the two mandate lists), the names the latest run
 * could not match, to add as an actor or as another's name, and the runs, each exportable as an
 * Excel workbook (redacted, or in full on purpose). The run itself is started from the module's
 * action, "Etsi vipupisteet".
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Download, Plus, Trash2 } from 'lucide-react';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation';
import { v2Json } from '../v3Fetch';
import useContentRefresh from '../../../hooks/useContentRefresh';
import PivotChoice from './PivotChoice';
import VotePanel from './VotePanel';

const Wrap = styled.section`
  padding: 24px 32px; display: flex; flex-direction: column; gap: 14px;
  @media (max-width: 640px) { padding: 20px 16px; }
  h3 { margin: 8px 0 0; font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.75); text-transform: uppercase; letter-spacing: 0.06em; }
  p { margin: 0; line-height: 1.5; }
`;
const Muted = styled.p`color: rgba(255,255,255,0.55); font-size: 13px;`;
const Estimates = styled.table`
  border-collapse: collapse; font-size: 14px; font-variant-numeric: tabular-nums;
  th, td { padding: 6px 14px 6px 0; text-align: left; }
  th { font-weight: 500; color: rgba(255,255,255,0.65); }
  td[data-over='true'] { color: #fca5a5; }
`;
const ErrorBox = styled.p`
  padding: 8px 12px; border-radius: 8px; font-size: 13px;
  border: 1px solid rgba(255,107,107,0.5); background: rgba(255,107,107,0.12); color: #ffd9d9;
`;
const Row = styled.div`
  display: grid; grid-template-columns: minmax(140px, 1fr) minmax(160px, 1.4fr) auto auto auto auto; gap: 8px; align-items: center;
  padding: 8px 10px; border-radius: 8px; border: 1px solid #2c3444; font-size: 14px;
  @media (max-width: 900px) { grid-template-columns: 1fr 1fr; }
  label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: rgba(255,255,255,0.8); }
  input[type='checkbox'] { width: 18px; height: 18px; }
`;
const Input = styled.input`
  box-sizing: border-box; width: 100%; padding: 8px 10px; border-radius: 8px; border: 1px solid #2c3444;
  background: #0f131a; color: #fff; font: inherit; font-size: 14px;
`;
const Select = styled.select`
  padding: 8px 10px; border-radius: 8px; border: 1px solid #2c3444; background: #0f131a; color: #fff; font: inherit; font-size: 13px;
`;
const Button = styled.button`
  all: unset; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; min-height: 36px;
  padding: 0 12px; border-radius: 8px; font-size: 13px; font-weight: 600;
  color: ${(p) => (p.$primary ? '#0b0d12' : 'rgba(255,255,255,0.85)')};
  background: ${(p) => (p.$primary ? '#c9a7ff' : 'transparent')};
  border: 1px solid ${(p) => (p.$primary ? '#c9a7ff' : '#2c3444')};
  &:disabled { opacity: 0.45; cursor: default; }
`;
const Line = styled.div`display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 14px;`;

const aliasesOf = (text) => [...new Set(String(text || '').split(',').map((x) => x.trim()).filter(Boolean))];

/** One actor, editable in place */
function ActorRow({ actor, onSave, onRemove }) {
  const { t } = useTranslation();
  const k = (key) => t(`ongelmakarttaV3.admin.${key}`);
  const [name, setName] = useState(actor.name);
  const [aliases, setAliases] = useState((actor.aliases || []).join(', '));
  const changed = name.trim() !== actor.name || aliasesOf(aliases).join(',') !== (actor.aliases || []).join(',');
  return (
    <Row>
      <Input aria-label={k('name')} value={name} onChange={(e) => setName(e.target.value)} />
      <Input aria-label={k('aliases')} placeholder={k('aliases')} value={aliases} onChange={(e) => setAliases(e.target.value)} />
      <label><input type="checkbox" checked={actor.aihe} onChange={(e) => onSave(actor.id, { aihe: e.target.checked })} />{k('aihe')}</label>
      <label><input type="checkbox" checked={actor.toimeksiantaja} onChange={(e) => onSave(actor.id, { toimeksiantaja: e.target.checked })} />{k('toimeksiantaja')}</label>
      <Button type="button" disabled={!changed || !name.trim()} onClick={() => onSave(actor.id, { name: name.trim(), aliases: aliasesOf(aliases) })}>{k('save')}</Button>
      <Button type="button" aria-label={k('remove')} onClick={() => onRemove(actor.id)}><Trash2 size={14} aria-hidden /></Button>
    </Row>
  );
}

/** @param {{ instanceId: string }} props */
export default function SystemicMapV3Admin({ instanceId }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`ongelmakarttaV3.admin.${key}`, vars);
  const [actors, setActors] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [runs, setRuns] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [newName, setNewName] = useState('');
  const [aliasTarget, setAliasTarget] = useState({});
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, r, e] = await Promise.all([
        v2Json(API_ENDPOINTS.SYSTEMIC_MAP_V3_ACTORS(instanceId)), v2Json(API_ENDPOINTS.SYSTEMIC_MAP_V3_RUNS(instanceId)), v2Json(API_ENDPOINTS.SYSTEMIC_MAP_V3_ESTIMATE(instanceId)),
      ]);
      setActors(a.actors);
      setUnmatched(a.unmatched || []);
      setRuns(r.runs);
      setEstimate(e);
      setError(null);
    } catch (err) {
      setError(k('loadFailed', { error: err.message }));
    }
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  // the preparation in the background moves the count of prepared root causes
  useContentRefresh(instanceId, ['systemic_map_v3_prepare'], load, { minIntervalMs: 5000 });

  const send = async (url, method, body) => {
    try {
      await v2Json(url, { method, headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
      await load();
    } catch (err) {
      setError(k('failed', { error: err.message }));
    }
  };
  const save = (id, patch) => send(API_ENDPOINTS.SYSTEMIC_MAP_V3_ACTOR(instanceId, id), 'PUT', patch);
  const remove = (id) => send(API_ENDPOINTS.SYSTEMIC_MAP_V3_ACTOR(instanceId, id), 'DELETE');
  const add = (name, extra = {}) => send(API_ENDPOINTS.SYSTEMIC_MAP_V3_ACTORS(instanceId), 'POST', { name, ...extra });
  const asAlias = (raw) => {
    const target = actors.find((a) => a.id === aliasTarget[raw]);
    if (target) save(target.id, { aliases: [...(target.aliases || []), raw] });
  };

  // a workbook through fetch, so the sign-in goes with it as with every other read
  const download = async (runId, full) => {
    try {
      const res = await fetch(API_ENDPOINTS.SYSTEMIC_MAP_V3_EXPORT(instanceId, runId, { full }), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `vipupisteet-${runId.slice(0, 8)}${full ? '-full' : ''}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(k('loadFailed', { error: err.message }));
    }
  };

  const total = (run, key) => Object.values(run.summary?.perType || {}).reduce((s, x) => s + (x[key] || 0), 0);
  return (
    <Wrap>
      {error ? <ErrorBox role="alert">{error}</ErrorBox> : null}
      <h3>{k('estimate.title')}</h3>
      {!estimate?.runs ? <Muted>{k('estimate.none', { limit: estimate?.limitMinutes ?? 15 })}</Muted> : (
        <>
          <Estimates>
            <thead><tr><th /><th>{k('estimate.judgeOff')}</th><th>{k('estimate.judgeOn')}</th></tr></thead>
            <tbody>
              {['sonnet', 'opus'].map((model) => (
                <tr key={model}>
                  <th>{k(`estimate.models.${model}`)}</th>
                  {[false, true].map((judge) => {
                    const o = estimate.options.find((x) => x.model === model && x.judge === judge);
                    return (
                      <td key={String(judge)} data-over={String(Boolean(o?.overLimit))}>
                        {o?.minutes == null ? k('estimate.unknown') : k(o.overLimit ? 'estimate.cellOver' : 'estimate.cell', { minutes: o.minutes, limit: estimate.limitMinutes })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Estimates>
          <Muted>{k('estimate.basis', { rootCauses: estimate.rootCauses, unread: estimate.unread, runs: estimate.runs, ownRuns: estimate.ownRuns, judgedRuns: estimate.judgedRuns, opusCalls: estimate.calls?.opus ?? 0, limit: estimate.limitMinutes })}</Muted>
        </>
      )}
      {estimate?.prepared != null ? <Muted>{k('estimate.prepared', { prepared: estimate.prepared, total: estimate.rootCauses })}</Muted> : null}

      <h3>{k('choice.title')}</h3>
      <PivotChoice instanceId={instanceId} onError={setError} />

      <h3>{k('vote.title')}</h3>
      <VotePanel instanceId={instanceId} onError={setError} />

      <h3>{k('actors')}</h3>
      <Muted>{k('actorsLead')}</Muted>
      {actors.map((a) => <ActorRow key={`${a.id}:${a.updated_at}`} actor={a} onSave={save} onRemove={remove} />)}
      <Line>
        <Input style={{ maxWidth: 320 }} aria-label={k('name')} placeholder={k('name')} value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button type="button" $primary disabled={!newName.trim()} onClick={() => { add(newName.trim()); setNewName(''); }}><Plus size={14} aria-hidden />{k('add')}</Button>
      </Line>

      <h3>{k('unmatched')}</h3>
      <Muted>{unmatched.length ? k('unmatchedLead') : k('noUnmatched')}</Muted>
      {unmatched.map((u) => (
        <Line key={u.id}>
          <strong>{u.raw}</strong>
          <Muted>{k('usedBy', { n: u.rootCauseIds.length })}</Muted>
          <Button type="button" onClick={() => add(u.raw)}>{k('addAsActor')}</Button>
          <Muted>{k('addAsAliasOf')}</Muted>
          <Select aria-label={k('choose')} value={aliasTarget[u.raw] || ''} onChange={(e) => setAliasTarget((s) => ({ ...s, [u.raw]: e.target.value }))}>
            <option value="">{k('choose')}</option>
            {actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
          <Button type="button" disabled={!aliasTarget[u.raw]} onClick={() => asAlias(u.raw)}>{k('save')}</Button>
        </Line>
      ))}

      <h3>{k('runs')}</h3>
      {!runs.length ? <Muted>{k('noRuns')}</Muted> : null}
      {runs.map((r) => (
        <Line key={r.id}>
          <span>{new Date(r.started_at).toLocaleString()}</span>
          <Muted>{k(`status.${r.status}`)}</Muted>
          {r.mode === 'preview' ? <Muted>{k('preview')}</Muted> : null}
          {r.model ? <Muted>{r.model}</Muted> : null}
          {r.summary?.perType ? <span>{k('runLine', { accepted: total(r, 'accepted'), generated: total(r, 'generated'), minutes: Math.round((r.summary.elapsedMs || 0) / 6000) / 10 })}</span> : null}
          {r.status !== 'running' ? (
            <>
              <Button type="button" onClick={() => download(r.id, false)}><Download size={14} aria-hidden />{k('exportXlsx')}</Button>
              <Button type="button" onClick={() => download(r.id, true)}>{k('exportFull')}</Button>
            </>
          ) : null}
        </Line>
      ))}
    </Wrap>
  );
}
